import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, GripVertical } from "lucide-react";

const TYPES = [
  { value: "text", label: "Text" },
  { value: "number", label: "Number" },
  { value: "date", label: "Date" },
  { value: "select", label: "Select (dropdown)" },
];

function slugify(s) {
  return (s || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_|_$/g, "")
    .slice(0, 40);
}

export default function CustomFieldsManager({ isAdmin }) {
  const [fields, setFields] = useState([]);
  const [draft, setDraft] = useState({ label: "", type: "text", options: "" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/settings/custom-fields");
      setFields(data.fields || []);
    } catch (_) {}
  };
  useEffect(() => { load(); }, []);

  const persist = async (next) => {
    setSaving(true);
    try {
      const { data } = await api.put("/settings/custom-fields", { fields: next });
      setFields(data.fields);
      toast.success("Custom fields saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
    setSaving(false);
  };

  const addField = async () => {
    if (!draft.label.trim()) {
      toast.error("Label required");
      return;
    }
    let key = slugify(draft.label);
    if (!key) key = `field_${fields.length + 1}`;
    if (fields.some((f) => f.key === key)) {
      toast.error(`Field key '${key}' already exists`);
      return;
    }
    const opts = draft.type === "select"
      ? draft.options.split(",").map((o) => o.trim()).filter(Boolean)
      : undefined;
    if (draft.type === "select" && (!opts || opts.length === 0)) {
      toast.error("Provide at least one option for a select field");
      return;
    }
    const next = [...fields, { key, label: draft.label.trim(), type: draft.type, options: opts }];
    await persist(next);
    setDraft({ label: "", type: "text", options: "" });
  };

  const removeField = async (key) => {
    if (!window.confirm(`Remove the "${key}" field? Existing data on employees will be kept in the database but stop being shown.`)) return;
    await persist(fields.filter((f) => f.key !== key));
  };

  return (
    <div className="space-y-4">
      {/* Existing fields */}
      <div className="border border-neutral-200">
        {fields.length === 0 ? (
          <p className="px-4 py-6 text-center text-sm text-neutral-600">
            No custom fields yet. Add things like Cost Center, Employee ID, Location, Start Date…
          </p>
        ) : (
          <ul>
            {fields.map((f) => (
              <li key={f.key} className="flex items-center gap-3 border-b border-neutral-100 px-4 py-3 last:border-0" data-testid={`custom-field-${f.key}`}>
                <GripVertical size={14} className="text-neutral-300"/>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{f.label}</p>
                  <p className="truncate font-mono text-[10px] text-neutral-500">{f.key}</p>
                </div>
                <Badge variant="outline" className="rounded-none border-neutral-300 text-[10px] uppercase">
                  {f.type}
                </Badge>
                {f.type === "select" && (
                  <span className="text-[10px] text-neutral-500" style={{ fontFamily: '"IBM Plex Mono", monospace' }}>
                    {f.options?.length} options
                  </span>
                )}
                <button
                  onClick={() => removeField(f.key)}
                  disabled={!isAdmin}
                  className="text-neutral-400 hover:text-[#C50F1F] disabled:opacity-30"
                  data-testid={`delete-custom-field-${f.key}`}
                  aria-label="Delete field"
                >
                  <Trash2 size={14}/>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Add new */}
      <div className="border border-neutral-200 p-4">
        <p className="mb-3 text-[10px] uppercase tracking-[0.2em] text-neutral-500" style={{ fontFamily: '"IBM Plex Mono", monospace' }}>
          Add a new field
        </p>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <div className="space-y-1">
            <Label className="text-[11px] uppercase tracking-wider text-neutral-500">Label</Label>
            <Input
              value={draft.label}
              onChange={(e) => setDraft({ ...draft, label: e.target.value })}
              placeholder="Cost Center"
              className="rounded-none border-neutral-300"
              data-testid="custom-field-label-input"
            />
            {draft.label && (
              <p className="font-mono text-[10px] text-neutral-500">key: {slugify(draft.label) || "—"}</p>
            )}
          </div>
          <div className="space-y-1">
            <Label className="text-[11px] uppercase tracking-wider text-neutral-500">Type</Label>
            <Select value={draft.type} onValueChange={(v) => setDraft({ ...draft, type: v })}>
              <SelectTrigger className="rounded-none border-neutral-300" data-testid="custom-field-type-select"><SelectValue/></SelectTrigger>
              <SelectContent>
                {TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {draft.type === "select" && (
            <div className="space-y-1 md:col-span-2">
              <Label className="text-[11px] uppercase tracking-wider text-neutral-500">Options (comma separated)</Label>
              <Input
                value={draft.options}
                onChange={(e) => setDraft({ ...draft, options: e.target.value })}
                placeholder="Full-time, Part-time, Contractor, Intern"
                className="rounded-none border-neutral-300"
                data-testid="custom-field-options-input"
              />
            </div>
          )}
        </div>
        <Button
          onClick={addField}
          disabled={!isAdmin || saving}
          className="mt-3 rounded-none bg-[#002FA7] text-white hover:bg-[#001F70]"
          data-testid="add-custom-field-button"
        >
          <Plus size={14} className="mr-2"/>
          Add field
        </Button>
      </div>
    </div>
  );
}
